<template>
    <section class="w-full">
        <header
            v-if="blok.TableTitle"
            class="mb-4 w-full"
            :class="titleClass"
            :style="{
                ...titleWrapStyle,
            }"
        >
            <h2 class="text-xl font-semibold leading-6 px-4 py-3" :style="titleStyle">
                {{ blok.TableTitle }}
            </h2>
        </header>

        <div class="w-full overflow-x-auto">
            <table
                class="w-full table-fixed border-separate border-spacing-0 eq-table"
                :style="{ '--col-count': String(colCount || 1), '--col-min': colMin, '--tbl-fs': tableFontSizeRem }"
            >
                <colgroup v-if="colCount > 0">
                    <col v-for="i in colCount" :key="i" style="width: calc(100% / var(--col-count))" />
                </colgroup>

                <thead v-if="tableHead.length">
                    <tr :class="[headerClass, 'text-white']" :style="headerStyle">
                        <th
                            v-for="(col, idx) in tableHead"
                            :key="idx"
                            scope="col"
                            class="th th-head"
                            :class="[
                                idx === 0 ? 'th-head-first' : '',
                                idx === 0 ? 'text-left' : 'text-center',
                                idx === 0 ? 'th-head-divider' : '',
                            ]"
                        >
                            {{ col }}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr v-for="(row, rIdx) in tableBody" :key="rIdx" :class="zebraClasses(rIdx)">
                        <td
                            v-for="(cell, cIdx) in row"
                            :key="cIdx"
                            class="td break-words whitespace-normal"
                            :class="cIdx === 0 ? 'text-left' : 'text-center'"
                            :style="cellStyle"
                            v-html="cell"
                        />
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { ITable, TableRow } from '@nuxt-monorepo/ui/types/components/Table';

const props = defineProps<{ blok: ITable }>();

const colMin = '140px';

type StyleObj = Record<string, string>;
const merge = (...o: Array<StyleObj | null | undefined>) => Object.assign({}, ...o.filter(Boolean));

const colorStyle = (c: { color?: string } | undefined, kind: 'text' | 'bg'): StyleObj | null =>
    c?.color ? (kind === 'text' ? { color: c.color } : { backgroundColor: c.color }) : null;

const fontSizeRem = (s?: string | number): StyleObj | null => {
    if (s === undefined || s === null || s === '' || isNaN(Number(s))) return null;

    const size = Number(s);
    if (size <= 0) return null;

    const safeSize = Math.min(Math.max(size, 0.5), 3);

    return { fontSize: `${safeSize}rem` };
};

const alignClass = (a?: string) => {
    const v = (a || '').toLowerCase();
    if (v === 'center') return 'text-center';
    if (v === 'right') return 'text-right';
    return 'text-left';
};

const titleClass = computed(() => alignClass(props.blok.TableTitleAlign));
const titleStyle = computed<StyleObj>(() =>
    merge(colorStyle(props.blok.TableTitleColor, 'text'), fontSizeRem(props.blok.TableTitleFontSize))
);
const titleWrapStyle = computed<StyleObj>(() => merge(colorStyle(props.blok.TableTitleBackground, 'bg')));

// default bg if editor didn't set a color
const headerClass = computed(() => ((props.blok as any)?.TableHeadBackground?.color ? '' : 'bg-primary'));
// inline styles override defaults when provided
const headerStyle = computed<StyleObj>(() =>
    merge(
        colorStyle((props.blok as any)?.TableHeadBackground, 'bg'),
        colorStyle((props.blok as any)?.TableHeadTextColor, 'text')
    )
);

const cellStyle = computed<StyleObj>(() => merge(colorStyle(props.blok.TableTextColor, 'text')));

const tableHead = computed(() => props.blok.Table?.thead?.map((h) => h.value?.trim() ?? '') || []);
const tableBody = computed(() =>
    (props.blok.Table?.tbody || []).map((row: TableRow) => (row.body || []).map((c) => c?.value ?? ''))
);
const colCount = computed(() => tableHead.value.length);

const rowStyleKey = computed(() => props.blok.RowStyles?.toLowerCase() ?? '');
const zebraClasses = (i: number) => {
    const rowDivider =
        'relative after:content-[""] after:absolute after:left-0 after:right-0 after:bottom-0 ' +
        'after:[height:2px] after:bg-slate-300 last:after:hidden';

    switch (rowStyleKey.value) {
        case 'white-gray':
            return `${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${rowDivider}`;
        case 'white-gray-no-border':
            return i % 2 === 0 ? 'bg-white' : 'bg-slate-50';
        case 'white-blue-no-border':
            return i % 2 === 0 ? 'bg-white' : 'bg-blue-50';
        case 'white':
            return 'bg-white';
        default:
            return `${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${rowDivider}`;
    }
};

const tableFontSizeRem = computed(() => {
    const raw = Number(props.blok.TableTextFontSize);
    if (Number.isNaN(raw) || raw <= 0) return '0.875rem';
    const safeSize = Math.min(Math.max(raw, 0.5), 3);
    return `${safeSize}rem`;
});
</script>

<style scoped>
.eq-table {
    --col-count: 1;
    --col-min: 140px;
    min-width: max(100%, calc(var(--col-count) * var(--col-min)));
    font-size: var(--tbl-fs, 0.875rem);
}

.th {
    @apply px-4 py-4 align-middle;
}

.th-head {
    @apply bg-transparent text-white font-semibold;
    @apply border-y border-r border-white/70;
    @apply md:border-0;
}

.th-head-first {
    @apply border-l border-white/70 md:border-l-0;
}

.th-head-divider {
    @apply md:border-r md:border-white/70;
}

.td {
    @apply px-4 py-4 align-middle text-slate-900;

    :deep(a) {
        @apply text-primary underline underline-offset-2 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-none;
        text-decoration-color: currentColor;
    }
}

@media (max-width: theme('screens.md')) {
    .eq-table {
        font-size: calc(var(--tbl-fs, 0.875rem) * 0.9);
    }
}

@media (max-width: theme('screens.sm')) {
    .eq-table {
        font-size: calc(var(--tbl-fs, 0.875rem) * 0.8);
    }
}
</style>
